import { getStorage } from '../../../utils/enhancedStorage'
import { FEATURES, MODULES } from '../constants/template-info'
import { LocalStorageData } from '../domains/contentful/LocalStorageDataWithExpiry'

import { captureException } from './sentryUtil'

export const getFromLocalStorageWithExpiry = (
  key: string,
  shouldParseJSON = true,
  shouldLogToSentry = true,
  isGlobal = true, // Default to global for backward compatibility
): string | null => {
  if (typeof localStorage === 'undefined') return null
  try {
    const now = new Date()

    // Use enhanced storage for locale support
    const storage = getStorage()
    const val = storage.getItem<string>(key, { isGlobal })

    if (val) {
      if (!shouldParseJSON) return val
      const data: LocalStorageData = JSON.parse(val)
      if (now.getTime() > data.expiryInMs) {
        storage.removeItem(key, { isGlobal })
        return null
      }
      return data.value
    }
  } catch (error) {
    if (shouldLogToSentry) {
      captureException('getFromLocalStorageWithExpiry error', {
        module: MODULES.LOCAL_STORAGE,
        feature: FEATURES.GET_FROM_LOCAL_STORAGE_WITH_EXPIRY,
        extraInfo: {
          // @i18n-ignore
          capturedFrom: 'Get in local storage with expiry function',
          localStorageKey: key,
          errorDetail: JSON.stringify(error),
        },
      })
    }
  }
  return null
}

/**
 * Enhanced version with explicit locale support
 * @param key - The key to retrieve
 * @param shouldParseJSON - Whether to parse the JSON wrapper (default: true)
 * @param shouldLogToSentry - Whether to log errors to Sentry (default: true)
 * @param isGlobal - Whether data is shared across languages (default: false)
 */
export const getFromLocalStorageWithExpiryLocalized = (
  key: string,
  shouldParseJSON = true,
  shouldLogToSentry = true,
  isGlobal = false, // Default to localized for new usage
): string | null => {
  return getFromLocalStorageWithExpiry(key, shouldParseJSON, shouldLogToSentry, isGlobal)
}
